import { definePreset } from '@primeng/themes';
import { ButtonDesignTokens } from '@primeng/themes/types/button';
import { CheckboxDesignTokens } from '@primeng/themes/types/checkbox';
import { TabsDesignTokens } from '@primeng/themes/types/tabs';
import { PaginatorDesignTokens } from '@primeng/themes/types/paginator';
import { MultiSelectDesignTokens } from '@primeng/themes/types/multiselect';
import { DataTableDesignTokens } from '@primeng/themes/types/datatable';
import { TextareaDesignTokens } from '@primeng/themes/types/textarea';
import { DialogDesignTokens } from '@primeng/themes/types/dialog';
import { TooltipDesignTokens } from '@primeng/themes/types/tooltip';
import { ToastDesignTokens } from '@primeng/themes/types/toast';
import { PanelMenuDesignTokens } from '@primeng/themes/types/panelmenu';
import { ToggleSwitchDesignTokens } from '@primeng/themes/types/toggleswitch';

import Material from '@primeng/themes/material';

export const AgiSalesPreset = definePreset(Material, {
  components: {
    checkbox: {
      root: {
        borderRadius: `{borderRadius.sm}`,
        checkedBackground: `{secondary.500}`,
        checkedBorderColor: `{secondary.500}`,
        checkedHoverBackground: `{secondary.500}`,
        checkedHoverBorderColor: `{secondary.600}`,
        checkedFocusBorderColor: `{indicator.info}`,
        checkedDisabledBorderColor: `{border.200}`,
        background: `{background.0}`,
        disabledBackground: `{background.0}`,
        borderColor: `{border.400}`,
        hoverBorderColor: `{secondary.500}`,
        focusBorderColor: `{indicator.info}`,
        invalidBorderColor: '{indicator.error}',
      },
      colorScheme: {
        light: {
          icon: {
            color: `{background.950}`,
            checkedColor: `{background.950}`,
            checkedHoverColor: `{background.950}`,
          },
        },
      },
    } as CheckboxDesignTokens,
    radiobutton: {
      root: {
        checkedBorderColor: `{secondary.500}`,
        checkedHoverBorderColor: `{secondary.500}`,
        checkedFocusBorderColor: `{indicator.info}`,
        hoverBorderColor: `{secondary.500}`,
      },
      colorScheme: {
        light: {
          icon: {
            checkedColor: `{secondary.500}`,
            checkedHoverColor: `{secondary.500}`,
          },
        },
      },
    } as CheckboxDesignTokens,
    button: {
      root: {
        borderRadius: '{borderRadius.sm}',
        paddingY: '{size.spacing.4}',
      },
      colorScheme: {
        light: {
          root: {
            danger: {
              background: '{error.500}',
              hoverBackground: '{error.400}',
              activeBackground: '{error.300}',
              borderColor: '{error.500}',
              hoverBorderColor: '{error.200}',
              activeBorderColor: '{error.300}',
              color: '{error.950}',
              hoverColor: '{error.950}',
              activeColor: '{error.950}',
              focusRing: {
                color: '{error.600}',
                shadow: 'none',
              },
            },
            success: {
              background: '{success.500}',
              hoverBackground: '{success.400}',
              activeBackground: '{success.300}',
              borderColor: '{success.500}',
              hoverBorderColor: '{success.200}',
              activeBorderColor: '{success.300}',
              color: '{text.0}',
              hoverColor: '{success.950}',
              activeColor: '{success.950}',
              focusRing: {
                color: '{success.600}',
                shadow: 'none',
              },
            },
            secondary: {
              background: '{gray.500}',
              hoverBackground: '{gray.400}',
              activeBackground: '{gray.300}',
              borderColor: '{gray.600}',
              hoverBorderColor: '{gray.200}',
              activeBorderColor: '{gray.300}',
              color: '{gray.950}',
              hoverColor: '{gray.950}',
              activeColor: '{gray.950}',
              focusRing: {
                color: '{gray.600}',
                shadow: 'none',
              },
              disabledBackground: '{text.200}',
              disabledColor: '{text.500}',
            },
            primary: {
              background: '{primary.700}',
              hoverBackground: '{primary.400}',
              activeBackground: '{primary.300}',
              borderColor: '{primary.500}',
              hoverBorderColor: '{primary.200}',
              activeBorderColor: '{primary.300}',
              color: '{text.0}',
              hoverColor: '{primary.950}',
              activeColor: '{primary.950}',
              focusRing: {
                color: '{primary.600}',
                shadow: 'none',
              },
              disabledBackground: '{text.200}',
              disabledColor: '{text.500}',
            },
          },
        },
      },
      css: ({ dt }: { dt: (param: string) => string }) => `
      .p-button-label {
        font-size: ${dt('size.spacing.7')};
      }
      .p-button:disabled,
      .p-button[disabled],
      .p-button.p-disabled {
        background: ${dt('text.200')} !important;
        border-color: ${dt('text.200')} !important;
        color: ${dt('text.500')} !important;
        opacity: 1 !important;
        cursor: not-allowed;
      }
      .p-button-danger {
        color: ${dt('text.0')} !important;
      }
      `,
    } as ButtonDesignTokens,
    dialog: {
      header: {
        padding: '{size.spacing.4}',
        borderBottom: '1px solid {border.100}',
      },
      css: () => `
      .p-dialog .p-dialog-header {
        padding: 1rem;
        border-bottom: 1px solid var(--p-border-100);
      }
      `,
    } as DialogDesignTokens,
    tabs: {
      tabpanel: { padding: '{size.spacing.9} 0' },
      activeBar: { height: '{borderRadius.sm}' },
      css: () => `
      .p-tablist-tab-list, .p-tabpanels {
        background: none;
      }
      `,
    } as TabsDesignTokens,
    paginator: {
      root: { gap: '{size.spacing.4}' },
      navButton: {
        borderRadius: '{borderRadius.sm}',
        selectedBackground: `{primary.700}`,
        selectedColor: '{text.0}',
        color: `{text.900}`,
        height: '{size.spacing.18}',
      },
      css: ({ dt }: { dt: (param: string) => string }) => `
      .p-paginator {
        width: fit-content;
        margin: auto;
        background: transparent;
      }

      .p-paginator-page, .p-paginator-prev, .p-paginator-next  {
        outline: 1px solid ${dt('border.100')};
        font-size: ${dt('size.spacing.7')};
      }

      .p-paginator-prev:disabled, .p-paginator-next:disabled {
        background: ${dt('text.100')};
      }
      `,
    } as PaginatorDesignTokens,
    multiselect: {
      css: ({ dt }: { dt: (v: string) => string }) => `
      .p-multiselect-label {
        font-size: ${dt('size.text.sm')};
        font-weight: 400;
      }
      .p-multiselect-header {
          position: relative;
          color: ${dt('text.700')}
      }
      .p-multiselect-header::before {
        content: "";
        position: absolute;
        top: 0;
        left: 50%;
        width: 95%;
        transform: translateX(-50%);
        height: 100%;
        border-bottom: 1px solid ${dt('border.100')};
        box-sizing: border-box;
        pointer-events: none;
      }

      .p-multiselect-option {
        color: ${dt('text.700')};
        font-size: ${dt('size.text.sm')};
      }
      .p-multiselect-empty-message {
        color: ${dt('text.700')};
        font-size: ${dt('size.text.sm')};
      }
      .p-multiselect .p-multiselect-overlay {
        border-radius: ${dt('borderRadius.md')};
        box-shadow: ${dt('shadow.hard.4')};
      }
      .p-multiselect-filter {
        padding: ${dt('size.spacing.2')};
        padding-right: 0px;
      }
      .p-multiselect-overlay {
        margin-block: ${dt('size.spacing.4')};
      }
      p-checkbox {
        display: flex;
      }
      `,
    } as MultiSelectDesignTokens,
    datatable: {
      css: () => `
      .p-inputtext-sm {
        font-weight: 400;
      }
    `,
    } as DataTableDesignTokens,
    textarea: {
      root: { invalidBorderColor: '{indicator.error}' },
    } as TextareaDesignTokens,
    tooltip: {
      root: {
        background: '{background.900}',
        color: '{text.0}',
        padding: '{size.spacing.6}',
        borderRadius: '{borderRadius.sm}',
        shadow: '{shadow.hard.2}',
      },
      arrow: {
        display: 'none',
      },
      css: () => `
        .p-tooltip-no-arrow .p-tooltip-arrow {
          display: none !important;
        }

        .p-tooltip .p-tooltip-text {
          font-size: var(--p-size-text-sm);
          line-height: 1.5;
        }
      `,
    } as TooltipDesignTokens,
    toast: {
      root: {
        background: '#FFFFFF',
        color: '{text.900}',
        borderRadius: '{borderRadius.sm}',
        shadow: '{shadow.hard.2}',
      },
      message: {
        background: 'transparent',
        color: '{text.900}',
      },
      css: ({ dt }: { dt: (v: string) => string }) => `
        .p-toast-message {
          background: #FFFFFF;
          -webkit-box-shadow: ${dt('shadow.hard.5')};
          -moz-box-shadow: ${dt('shadow.hard.5')};
          box-shadow: ${dt('shadow.hard.5')};
        }

        .p-toast-message .p-toast-message-content .p-toast-message-icon {
          font-size: 1.5rem;
        }

        .p-toast-message-success .p-toast-message-icon {
          color: var(--p-success-500);
        }

        .p-toast-message-info .p-toast-message-icon {
          color: var(--p-info-500);
        }

        .p-toast-message-warn .p-toast-message-icon {
          color: var(--p-warning-500);
        }

        .p-toast-message-error .p-toast-message-icon {
          color: var(--p-error-500);
        }
      `,
    } as ToastDesignTokens,
    panelmenu: {
      item: { gap: '{size.spacing.8}' },
      root: { gap: '{size.spacing.4}' },
      css: ({ dt }: { dt: (v: string) => string }) => `
      .p-panelmenu-item.p-focus > .p-panelmenu-item-content {
        background: none;
      }

      ul.p-panelmenu-submenu {
        padding: 0;
        display: flex;
        flex-direction: column;
        gap: ${dt('size.spacing.4')};
      }

      div.p-panelmenu-content-container {
        margin-top: ${dt('size.spacing.4')};
      }
      `,
    } as PanelMenuDesignTokens,
    toggleswitch: {
      root: {
        width: '2.5rem',
        height: '1.5rem',
        borderRadius: '30px',
        gap: '0.25rem',
        shadow: '{form.field.shadow}',
        focusRing: {
          width: '{focus.ring.width}',
          style: '{focus.ring.style}',
          color: '{focus.ring.color}',
          offset: '{focus.ring.offset}',
          shadow: '{focus.ring.shadow}',
        },
        borderWidth: '1px',
        borderColor: 'transparent',
        hoverBorderColor: 'transparent',
        checkedBorderColor: 'transparent',
        checkedHoverBorderColor: 'transparent',
        invalidBorderColor: '{form.field.invalid.border.color}',
        transitionDuration: '{form.field.transition.duration}',
        slideDuration: '0.2s',
      },
      handle: {
        borderRadius: '50%',
        size: '1rem',
      },
      colorScheme: {
        light: {
          root: {
            background: '{surface.300}',
            disabledBackground: '{form.field.disabled.background}',
            hoverBackground: '{surface.400}',
            checkedBackground: '{secondary.500}',
            checkedHoverBackground: '{secondary.500}',
          },
          handle: {
            background: '{surface.0}',
            disabledBackground: '{form.field.disabled.color}',
            hoverBackground: '{surface.0}',
            checkedBackground: '{surface.0}',
            checkedHoverBackground: '{surface.0}',
            color: '{text.muted.color}',
            hoverColor: '{text.color}',
            checkedColor: '{primary.color}',
            checkedHoverColor: '{primary.hover.color}',
          },
        },
        dark: {
          root: {
            background: '{surface.700}',
            disabledBackground: '{surface.600}',
            hoverBackground: '{surface.600}',
            checkedBackground: '{primary.color}',
            checkedHoverBackground: '{primary.hover.color}',
          },
          handle: {
            background: '{surface.400}',
            disabledBackground: '{surface.900}',
            hoverBackground: '{surface.300}',
            checkedBackground: '{surface.900}',
            checkedHoverBackground: '{surface.900}',
            color: '{surface.900}',
            hoverColor: '{surface.800}',
            checkedColor: '{primary.color}',
            checkedHoverColor: '{primary.hover.color}',
          },
        },
      },
    } as ToggleSwitchDesignTokens,
  },
  semantic: {
    primary: {
      50: '{blue.50}',
      100: '{blue.100}',
      200: '{blue.200}',
      300: '{blue.300}',
      400: '{blue.400}',
      500: '{blue.500}',
      600: '{blue.600}',
      700: '{blue.700}',
      800: '{blue.800}',
      900: '{blue.900}',
      950: '{blue.950}',
    },
    colorScheme: {
      light: {
        focusRing: {
          shadow: '0 0 1px 4px {surface.200}',
        },
        surface: {
          0: '#ffffff',
          50: '{slate.50}',
          100: '{slate.100}',
          200: '{slate.200}',
          300: '{slate.300}',
          400: '{slate.400}',
          500: '{slate.500}',
          600: '{slate.600}',
          700: '{slate.700}',
          800: '{slate.800}',
          900: '{slate.900}',
          950: '{slate.950}',
        },
        primary: {
          color: '{primary.500}',
          contrastColor: '#ffffff',
          hoverColor: '{primary.400}',
          activeColor: '{primary.300}',
        },
        highlight: {
          background: 'color-mix(in srgb, {primary.color}, transparent 88%)',
          focusBackground:
            'color-mix(in srgb, {primary.color}, transparent 76%)',
          color: '{primary.700}',
          focusColor: '{primary.800}',
        },
        mask: {
          background: 'rgba(0,0,0,0.32)',
          color: '{surface.200}',
        },
        formField: {
          background: '{surface.0}',
          disabledBackground: '{surface.300}',
          filledBackground: '{surface.100}',
          filledHoverBackground: '{surface.200}',
          filledFocusBackground: '{surface.100}',
          borderColor: '{surface.400}',
          hoverBorderColor: '{surface.900}',
          focusBorderColor: '{primary.color}',
          invalidBorderColor: '{error.800}',
          color: '{surface.900}',
          disabledColor: '{surface.600}',
          placeholderColor: '{surface.600}',
          invalidPlaceholderColor: '{error.800}',
          floatLabelColor: '{surface.600}',
          floatLabelFocusColor: '{primary.600}',
          floatLabelActiveColor: '{surface.600}',
          floatLabelInvalidColor: '{form.field.invalid.placeholder.color}',
          iconColor: '{surface.600}',
          shadow: 'none',
        },
        text: {
          color: '{surface.900}',
          hoverColor: '{surface.900}',
          mutedColor: '{surface.600}',
          hoverMutedColor: '{surface.600}',
        },
        content: {
          background: '{surface.0}',
          hoverBackground: '{surface.100}',
          borderColor: '{surface.300}',
          color: '{text.color}',
          hoverColor: '{text.hover.color}',
        },
        overlay: {
          select: {
            background: '{surface.0}',
            borderColor: '{surface.0}',
            color: '{text.color}',
          },
          popover: {
            background: '{surface.0}',
            borderColor: '{surface.0}',
            color: '{text.color}',
          },
          modal: {
            background: '{surface.0}',
            borderColor: '{surface.0}',
            color: '{text.color}',
          },
        },
        list: {
          option: {
            focusBackground: '{surface.100}',
            selectedBackground: '{highlight.background}',
            selectedFocusBackground: '{highlight.focus.background}',
            color: '{text.color}',
            focusColor: '{text.hover.color}',
            selectedColor: '{highlight.color}',
            selectedFocusColor: '{highlight.focus.color}',
            icon: {
              color: '{surface.600}',
              focusColor: '{surface.600}',
            },
          },
          optionGroup: {
            background: 'transparent',
            color: '{text.color}',
          },
        },
        navigation: {
          item: {
            focusBackground: '{surface.100}',
            activeBackground: '{surface.200}',
            color: '{text.color}',
            focusColor: '{text.hover.color}',
            activeColor: '{text.hover.color}',
            icon: {
              color: '{surface.600}',
              focusColor: '{surface.600}',
              activeColor: '{surface.600}',
            },
          },
          submenuLabel: {
            background: 'transparent',
            color: '{text.color}',
          },
          submenuIcon: {
            color: '{surface.600}',
            focusColor: '{surface.600}',
            activeColor: '{surface.600}',
          },
        },
      },
      dark: {
        focusRing: {
          shadow: '0 0 1px 4px {surface.700}',
        },
        surface: {
          0: '#ffffff',
          50: '{zinc.50}',
          100: '{zinc.100}',
          200: '{zinc.200}',
          300: '{zinc.300}',
          400: '{zinc.400}',
          500: '{zinc.500}',
          600: '{zinc.600}',
          700: '{zinc.700}',
          800: '{zinc.800}',
          900: '{zinc.900}',
          950: '{zinc.950}',
        },
        primary: {
          color: '{primary.400}',
          contrastColor: '{surface.900}',
          hoverColor: '{primary.300}',
          activeColor: '{primary.200}',
        },
        highlight: {
          background: 'color-mix(in srgb, {primary.400}, transparent 84%)',
          focusBackground: 'color-mix(in srgb, {primary.400}, transparent 76%)',
          color: 'rgba(255,255,255,.87)',
          focusColor: 'rgba(255,255,255,.87)',
        },
        mask: {
          background: 'rgba(0,0,0,0.6)',
          color: '{surface.200}',
        },
        formField: {
          background: '{surface.950}',
          disabledBackground: '{surface.700}',
          filledBackground: '{surface.800}',
          filledHoverBackground: '{surface.700}',
          filledFocusBackground: '{surface.800}',
          borderColor: '{surface.600}',
          hoverBorderColor: '{surface.400}',
          focusBorderColor: '{primary.color}',
          invalidBorderColor: '{error.300}',
          color: '{surface.0}',
          disabledColor: '{surface.400}',
          placeholderColor: '{surface.400}',
          invalidPlaceholderColor: '{error.300}',
          floatLabelColor: '{surface.400}',
          floatLabelFocusColor: '{primary.color}',
          floatLabelActiveColor: '{surface.400}',
          floatLabelInvalidColor: '{form.field.invalid.placeholder.color}',
          iconColor: '{surface.400}',
          shadow: 'none',
        },
        text: {
          color: '{surface.0}',
          hoverColor: '{surface.0}',
          mutedColor: '{surface.400}',
          hoverMutedColor: '{surface.400}',
        },
        content: {
          background: '{surface.900}',
          hoverBackground: '{surface.800}',
          borderColor: '{surface.700}',
          color: '{text.color}',
          hoverColor: '{text.hover.color}',
        },
        overlay: {
          select: {
            background: '{surface.900}',
            borderColor: '{surface.900}',
            color: '{text.color}',
          },
          popover: {
            background: '{surface.900}',
            borderColor: '{surface.900}',
            color: '{text.color}',
          },
          modal: {
            background: '{surface.900}',
            borderColor: '{surface.900}',
            color: '{text.color}',
          },
        },
        list: {
          option: {
            focusBackground: '{surface.800}',
            selectedBackground: '{highlight.background}',
            selectedFocusBackground: '{highlight.focus.background}',
            color: '{text.color}',
            focusColor: '{text.hover.color}',
            selectedColor: '{highlight.color}',
            selectedFocusColor: '{highlight.focus.color}',
            icon: {
              color: '{surface.400}',
              focusColor: '{surface.400}',
            },
          },
          optionGroup: {
            background: 'transparent',
            color: '{text.muted.color}',
          },
        },
        navigation: {
          item: {
            focusBackground: '{surface.800}',
            activeBackground: '{surface.700}',
            color: '{text.color}',
            focusColor: '{text.hover.color}',
            activeColor: '{text.hover.color}',
            icon: {
              color: '{surface.400}',
              focusColor: '{surface.400}',
              activeColor: '{surface.400}',
            },
          },
          submenuLabel: {
            background: 'transparent',
            color: '{text.muted.color}',
          },
          submenuIcon: {
            color: '{surface.400}',
            focusColor: '{surface.400}',
            activeColor: '{surface.400}',
          },
        },
      },
    },
  },
  primitive: {
    shadow: {
      hard: {
        1: '-2px 2px 8px 0px #26262633',
        2: '0px 3px 10px 0px #26262633',
        3: '2px 2px 8px 0px #26262633',
        4: '0px -3px 10px 0px #26262633',
        5: '0px 2px 10px 0px #2626261A',
      },
      soft: {
        1: '0px 0px 10px 0px #2626261A',
        2: '0px 0px 20px 0px #2626261A',
        3: '0px 0px 30px 0px #2626261A',
        4: '0px 0px 40px 0px #26262633',
      },
    },
    size: {
      spacing: {
        0: '0px',
        1: '2px',
        2: '4px',
        3: '6px',
        4: '8px',
        5: '10px',
        6: '12px',
        7: '14px',
        8: '16px',
        9: '18px',
        10: '20px',
        11: '22px',
        12: '24px',
        13: '26px',
        14: '28px',
        15: '30px',
        16: '32px',
        17: '34px',
        18: '36px',
      },
      text: {
        xs2: '10px',
        xs: '12px',
        sm: '14px',
        md: '16px',
        lg: '18px',
        xl: '20px',
        xl2: '24px',
        xl3: '30px',
        xl4: '36px',
        xl5: '48px',
        xl6: '60px',
      },
    },
    borderRadius: {
      none: '0',
      xs: '2px',
      sm: '4px',
      md: '6px',
      lg: '8px',
      xl: '12px',
    },
    blue: {
      50: '#EFF6FF',
      100: '#DBEAFE',
      200: '#BFDBFE',
      300: '#93C5FD',
      400: '#60A5FA',
      500: '#3B82F6',
      600: '#2563EB',
      700: '#1D4ED8',
      800: '#1E40AF',
      900: '#1E3A8A',
      950: '#052952',
    },
    info: {
      50: '#e6f3f7',
      100: '#cce7ef',
      200: '#99cfdf',
      300: '#66b8cf',
      400: '#33a0bf',
      500: '#0088af',
      600: '#006d8c',
      700: '#00516a',
      800: '#075A83',
      900: '#001c24',
      950: '#000e12',
    },
    secondary: {
      50: '#E6F6FB',
      100: '#CCECF7',
      200: '#99D9EF',
      300: '#66C6E7',
      400: '#33B3DF',
      500: '#0B8DCD',
      600: '#0971A4',
      700: '#07557B',
      800: '#053952',
      900: '#031D29',
      950: '#021620',
    },
    warning: {
      50: '#FFF9F5',
      100: '#FFE7D5',
      200: '#FECDAA',
      300: '#FDAD74',
      400: '#FB954B',
      500: '#E77828',
      600: '#D76C1F',
      700: '#B45A1A',
      800: '#824417',
      900: '#6C3813',
      950: '#542D12',
    },
    success: {
      50: '#CAFFE8',
      100: '#A2F1C0',
      200: '#84D3A2',
      300: '#66B584',
      400: '#489766',
      500: '#348352',
      600: '#2A7948',
      700: '#206F3E',
      800: '#166534',
      900: '#14532D',
      950: '#1B3224',
    },
    error: {
      50: '#FEE2E2',
      100: '#FECACA',
      200: '#FCA5A5',
      300: '#F87171',
      400: '#EF4444',
      500: '#E63535',
      600: '#DC2626',
      700: '#B91C1C',
      800: '#991B1B',
      900: '#7F1D1D',
      950: '#531313',
    },
    background: {
      0: '#FFFFFF',
      50: '#EFF8FB',
      100: '#E0F1F7',
      200: '#C1E3EF',
      300: '#A2D5E7',
      400: '#83C7DF',
      500: '#64B9D7',
      600: '#509AAC',
      700: '#3C7481',
      800: '#284D56',
      900: '#14272B',
      950: '#0A1416',
    },
    text: {
      0: '#FEFEFF',
      50: '#F5F5F5',
      100: '#E5E5E5',
      200: '#DBDBDC',
      300: '#D4D4D4',
      400: '#A3A3A3',
      500: '#8C8C8C',
      600: '#737373',
      700: '#525252',
      800: '#404040',
      900: '#262627',
      950: '#171717',
    },
    border: {
      50: '#F5F5F5',
      100: '#E5E5E5',
      200: '#DBDBDC',
      300: '#D4D4D4',
      400: '#A3A3A3',
      500: '#8C8C8C',
      600: '#737373',
      700: '#525252',
      800: '#404040',
      900: '#262627',
      950: '#171717',
    },
    indicator: {
      info: '#5399EC',
      error: '#B91C1C',
      primary: '#373737',
    },
    bluegray: {
      50: '#F8FAFC',
      100: '#F1F5F9',
      200: '#E2E8F0',
      300: '#CBD5E1',
      400: '#94A3B8',
      500: '#64748B',
      600: '#475569',
      700: '#334155',
      800: '#1E293B',
      900: '#0F172A',
    },
  },
});
